import React, { useEffect, useState } from 'react';
import { Box, CircularProgress } from '@mui/material';
import DsasButton from '../../../components/DsasButton/DsasButton.js';
import { useLocation, useNavigate } from 'react-router-dom';
import { checkExistingPlayer, exportPlayers, fetchPlayers } from '../../../../api/index.js';
import PlayerSearch from './components/PlayerSearch';
import PlayerResults from './components/PlayerResults';
import PlayerCheckModal from '../create/PlayerCheckModal.js';
import Filters from '../../../components/Filters.js';
import { getUserProvider } from '../../../../actions/auth.js';
import useFusionMode from '../../../hooks/useFusionMode.js';
import useStyles from '../../../../styles/global-mui.js';
////////  ICONS /////////
import { faUserPlus } from '@fortawesome/free-solid-svg-icons'
import { Can } from '../../../../permissions/Can.js';
import { PERMISSIONS } from '../../../../permissions/permissionKeys.js';
import { usePermissions } from '../../../../permissions/usePermissions.js';

const initialPlayer = {
  lastname: '',
  firstname: '',
  discipline: '',
  provider: getUserProvider(),
};

const GetPlayers = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [mode, setMode] = useState('normal'); // Modes : 'normal', 'fusion'

  const [players, setPlayers] = useState();
  const [loading, setLoading] = useState(false);
  const [playerData, setPlayerData] = useState(initialPlayer);
  const [filters, setFilters] = useState({});
  const [existingPlayers, setExistingPlayers] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCheckComplete, setIsCheckComplete] = useState(false);
  const [csvFilters, setCSVFilters] = useState({});

  /**
   * competition
: 
"6911a2cc5b79cae405f2e717"
discipline
: 
"soccer"
endDate
: 
Fri Jul 31 2026 00:00:00 GMT+0200 (heure d’été d’Europe centrale) {}
provider
: 
"dsas"
season
: 
"2025/2026"
startDate
: 
Fri Aug 01 2025 00:00:00 GMT+0200 (heure d’été d’Europe centrale) {}
teams
: 
"6911a2ef5b79cae405f2e725"
   */

  const { isFusionMode, FusionToggle, FusionZones, actions } = useFusionMode(players);

  const { getPermission } = usePermissions();
  const providerPermission = getPermission(PERMISSIONS.FILTER_PROVIDER);

  const filtersConfig = [
    { key: 'provider', name: 'provider', fullAccess:providerPermission },
    { key: 'discipline', name: 'discipline' },
    { key: 'season', name: 'season', isDateCustomizable : true },
    { key: 'competition', name: 'competition' },
    { key: 'teams', name: 'teams' },
  ];

  const searchParams = new URLSearchParams(location.search);
  const searchMode = searchParams.get('searchMode') || 'filters';

  useEffect(() => {
    if (searchMode === 'filters') {
      setPlayerData(initialPlayer);
    }

    setPlayers(undefined)
  }, [searchMode]);

  useEffect(() => {
    if (searchMode === 'simple') {
      const lastname = searchParams.get('lastname') || '';
      const firstname = searchParams.get('firstname') || '';
      const discipline = searchParams.get('discipline') || '';
      const provider = searchParams.get('provider') || getUserProvider();
      setPlayerData({ lastname, firstname, discipline, provider });
    }
  }, [location.search, searchMode]);

  useEffect(() => {
    if (!isValidCheckExistingPlayer()) return;

    handleCheckExistingPlayer();
  }, [playerData]);

  const handleApplyFilters = async (filters) => {
    //setPlayerData({ lastname, firstname, discipline, provider });
    setLoading(true);
    try {
      const response = await fetchPlayers(filters);
      setPlayers(response.data);
    } catch (error) {
      console.error('Failed to fetch players:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPlayerData((prev) => ({ ...prev, [name]: value }));

    const params = new URLSearchParams(location.search);
    params.set(name, value);
    navigate(`?${params.toString()}`, { replace: true });
  };

  const handleCheckExistingPlayer = async () => {
    setPlayers([]);

    if (!isValidCheckExistingPlayer()) return;

    const { firstname, lastname, discipline } = playerData;
    const results = await checkExistingPlayer({ firstname, lastname, discipline });
    const similarPlayers = results.data;

    if (!similarPlayers.length) {
      setIsCheckComplete(true);
      setIsModalOpen(false);
    } else {
      const sameProviderPlayers = similarPlayers.filter((p) => p.provider === playerData.provider);
      const otherProviderPlayers = similarPlayers.filter((p) => p.provider !== playerData.provider);

      if (sameProviderPlayers.length) {
        setPlayers(sameProviderPlayers);
      } else if (otherProviderPlayers.length) {
        setExistingPlayers(otherProviderPlayers);
        setIsModalOpen(true);
      }
    }
  };

  const isValidCheckExistingPlayer = () => {

    return searchMode === 'simple' ? 
                      (playerData 
                      && playerData.lastname 
                      && playerData.discipline 
                      && playerData.lastname.length >= 3 )
                      :
                      (filters
                      && filters.discipline 
                      && filters.competition 
                      && filters.teams && filters.teams.length>0);
  }

  const handleExportCSV = async () => {
    const response = await exportPlayers(csvFilters);

    if (response.status !== 200) {
      throw new Error('Erreur lors du téléchargement du CSV');
    }

    const csvContent = await response.data;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', 'players.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSelectOtherPlayer = (player) => {
    const copyPlayer = {
      ...player,
      provider: playerData.provider,
      teamsSeason: [],
      teams: [],
    };

    navigate(`/sports/players/create`, { state: { copyPlayer } });
    setIsCheckComplete(true);
    setIsModalOpen(false);
  };

  const handleConfirmNone = () => {
    setIsCheckComplete(true);
    setIsModalOpen(false);
    navigate(`/sports/players/create`, { state: { copyPlayer: { ...playerData, teamsSeason: [], teams: [] } } });
  };

  const removePlayerFromList = (id) => {
    setPlayers(s => s.filter(p => p._id !== id))
  }

  const classes = useStyles();

  return (
    <Box>

      {/*PAGE TITLE*/}
      <Box className='centered-medium-wrapper'>
        <h1 className='page-title'>Gestion des joueurs</h1>
      </Box>
      <Box className='medium-separator'></Box>

      {/*FUSION MODE BUTTON*/}
      <Box className='centered-medium-wrapper'>
        <Can permission={PERMISSIONS.SPORT_PLAYER_HOME_BUTTON_FUSION}>
          <FusionToggle />
        </Can>
      </Box>
      <Box className='medium-separator'></Box>

      {isFusionMode  && <FusionZones />}

      {/*CREATE PLAYER BUTTON*/}
      <Box className='centered-medium-wrapper'>
        <Can permission={PERMISSIONS.SPORT_PLAYER_HOME_BUTTON_CREATE}>
          <DsasButton label="Créer un joueur" icon={faUserPlus} onClick={() => navigate(`/sports/players/create`)} variant = 'secondary' />
        </Can>
      </Box>
      <Box className='medium-separator'></Box>

      {/*SEARCH SECTION*/}
      <Box className='centered-medium-wrapper'>
        {/*SEARCH BY NAME BUTTON*/}
        <DsasButton label="Rechercher par nom" onClick={() => navigate('?searchMode=simple')} variant = 'primarysub' focused = {searchMode === 'simple'} />
        {/*SEARCH BY TEAM BUTTON*/}
        <DsasButton label="Rechercher par équipe" onClick={() => navigate('?searchMode=filters')} variant = 'primarysub' focused = {searchMode !== 'simple'} />
      </Box>
      {/*SEARCH FILTERS*/}
      <Box className='padding-wrapper'>
      {searchMode === 'simple' ? (
        <PlayerSearch playerData={playerData} onChange={handleChange} className={classes.customInput} />
      ) : (
        <Filters filtersConfig={filtersConfig} onApplyFilters={handleApplyFilters} setCSVFilters={setCSVFilters} onReset={() => setPlayers([])} className={classes.customInput} setFiltersCallback={setFilters}/>
      )}
      </Box>

      {loading ? (
        <CircularProgress />
      ) : (
        <PlayerResults 
            showCreate={searchMode === 'simple' && isValidCheckExistingPlayer()} 
            handleSelectOtherPlayer={() => handleSelectOtherPlayer(playerData)}
            players={players} actions={actions} onExportCSV={handleExportCSV} removePlayerFromList={removePlayerFromList} 
            exportResult={searchMode !== 'simple'}
            showExport={!isFusionMode} showPicture={!isFusionMode}
            searchMode={searchMode}
            fieldsValidator={isValidCheckExistingPlayer}
            />
      )}

      <PlayerCheckModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        results={existingPlayers}
        onSelectOtherPlayer={handleSelectOtherPlayer}
        onConfirmNone={handleConfirmNone}
        actualProvider={playerData.provider}
      />
    </Box>
  );
};

export default GetPlayers;
